"use client";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { IconCheck, IconPlus, IconSearch } from "./icons.js";
import { tagColorStyle } from "../palette.js";
const norm = (text) => text.toLocaleLowerCase().replace(/ё/g, "е").trim();
export const matches = (option, query) => !query || norm(`${option.label} ${option.hint ?? ""} ${option.group ?? ""}`).includes(norm(query));
/** Group options by first appearance of their group, keeping order inside a group. */
export function grouped(options) {
    const order = [];
    const by = new Map();
    for (const option of options) {
        const key = option.group ?? "";
        if (!by.has(key)) {
            by.set(key, []);
            order.push(key);
        }
        by.get(key).push(option);
    }
    return order.flatMap(key => by.get(key));
}
/**
 * Filtering, highlight and picking shared by the list of Select / MultiSelect (search in the panel) and Combobox (search in the field).
 * Disabled options are skipped by the arrows and cannot be picked; «create» is the last row while the query matches no label.
 */
export function useOptionList({ options, query, selected, create, onPick }) {
    const shown = useMemo(() => grouped(options.filter(option => matches(option, query))), [options, query]);
    const canCreate = !!create && query.trim() !== "" && !options.some(option => norm(option.label) === norm(query));
    const count = shown.length + (canCreate ? 1 : 0);
    const firstSelected = shown.findIndex(option => selected.has(option.value));
    const [highlight, setHighlight] = useState(Math.max(0, firstSelected));
    useEffect(() => { setHighlight(index => Math.min(index, Math.max(0, count - 1))); }, [count]);
    function step(from, delta) {
        for (let i = 1; i <= count; i++) {
            const next = (from + delta * i + count * i) % count;
            if (next >= shown.length || !shown[next].disabled)
                return next;
        }
        return from;
    }
    const move = (delta) => { if (count)
        setHighlight(index => step(index, delta)); };
    function choose(index) {
        if (index < shown.length) {
            if (!shown[index].disabled)
                onPick(shown[index]);
        }
        else if (canCreate)
            create.onCreate(query.trim());
    }
    /** Back to the chosen option (or the first) — when a list opens again. */
    const reset = () => setHighlight(Math.max(0, firstSelected));
    return { shown, canCreate, count, highlight, setHighlight, move, choose, reset };
}
/** The options of a list: group headings, check, colour dot, icon, label, hint on the right; «create» last. */
export function OptionItems({ list, listId, label, selected, multiple, emptyText, create, query, focusable, textData }) {
    const { shown, canCreate, count, highlight, setHighlight, choose } = list;
    const optionId = (index) => optionDomId(listId, index);
    useEffect(() => { document.getElementById(optionId(highlight))?.scrollIntoView?.({ block: "nearest" }); });
    let lastGroup;
    return _jsxs("ul", { id: listId, role: "listbox", "aria-label": label, "aria-multiselectable": multiple || undefined, tabIndex: focusable ? 0 : -1, "aria-activedescendant": focusable && count ? optionId(highlight) : undefined, className: "jui-listbox", children: [!shown.length && !canCreate && _jsx("li", { className: "jui-option-empty", role: "presentation", children: emptyText }), shown.map((option, index) => {
                const heading = option.group && option.group !== lastGroup ? option.group : null;
                lastGroup = option.group;
                const isSelected = selected.has(option.value);
                return [
                    heading && _jsx("li", { role: "presentation", className: "jui-option-group", children: heading }, `g-${heading}`),
                    _jsxs("li", { id: optionId(index), role: "option", "aria-selected": isSelected, "aria-disabled": option.disabled || undefined, className: "jui-option", title: option.title, "data-highlighted": highlight === index || undefined, onMouseMove: () => { if (highlight !== index)
                            setHighlight(index); }, onMouseDown: event => event.preventDefault(), onClick: () => choose(index), children: [_jsx(IconCheck, { className: "jui-option-check", style: { opacity: isSelected ? 1 : 0 } }), option.color && _jsx("span", { className: "jui-dot", style: tagColorStyle(option.color) }), option.icon, _jsx("span", { className: "jui-option-label", ...textData, children: option.label }), option.hint && _jsx("span", { className: "jui-option-hint", ...textData, children: option.hint })] }, option.value),
                ];
            }), canCreate && _jsxs("li", { id: optionId(shown.length), role: "option", "aria-selected": "false", className: "jui-option jui-option--create", "data-highlighted": highlight === shown.length || undefined, onMouseMove: () => setHighlight(shown.length), onMouseDown: event => event.preventDefault(), onClick: () => choose(shown.length), children: [_jsx(IconPlus, { small: true }), _jsx("span", { className: "jui-option-label", children: create.label(query.trim()) })] })] });
}
export const optionDomId = (listId, index) => `${listId}-o${index}`;
/** The floating list of Select / MultiSelect: search, groups, keyboard (↑ ↓ Home End Enter Esc), optional «create». */
export function ListPopover({ listId, label, options, selected, multiple, searchable, searchPlaceholder, emptyText, create, onPick, onClose, style, panel, container, footer, theme, density }) {
    const [query, setQuery] = useState("");
    const list = useOptionList({ options, query, selected, create, onPick });
    const { count, highlight, setHighlight, move, choose } = list;
    useEffect(() => { if (!searchable)
        panel.current?.querySelector("[role=listbox]")?.focus(); }, [searchable, panel]);
    function keyDown(event) {
        if (event.key === "ArrowDown") {
            event.preventDefault();
            move(1);
        }
        else if (event.key === "ArrowUp") {
            event.preventDefault();
            move(-1);
        }
        else if (event.key === "Home" && !searchable) {
            event.preventDefault();
            setHighlight(0);
        }
        else if (event.key === "End" && !searchable) {
            event.preventDefault();
            setHighlight(Math.max(0, count - 1));
        }
        else if (event.key === "Enter") {
            event.preventDefault();
            if (count)
                choose(highlight);
        }
        else if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            onClose();
        }
        else if (event.key === "Tab")
            onClose();
    }
    return createPortal(_jsxs("div", { ref: panel, className: "jui-popover", "data-theme": theme, "data-density": density, "data-up": style?.bottom !== undefined || undefined, style: style, role: "dialog", "aria-label": label, onKeyDown: keyDown, children: [searchable && _jsxs("label", { className: "jui-popover-search", children: [_jsx(IconSearch, { small: true }), _jsx("input", { autoFocus: true, role: "combobox", "aria-label": `${label}: поиск`, "aria-controls": listId, "aria-expanded": "true", "aria-autocomplete": "list", "aria-activedescendant": count ? optionDomId(listId, highlight) : undefined, placeholder: searchPlaceholder, value: query, onChange: event => { setQuery(event.target.value); setHighlight(0); } })] }), _jsx(OptionItems, { list: list, listId: listId, label: label, selected: selected, multiple: multiple, emptyText: emptyText, create: create, query: query, focusable: !searchable }), footer && _jsx("div", { className: "jui-popover-foot", children: footer })] }), container);
}
